import { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { toast } from 'sonner';
import { Loader2, Mail, X, Plus } from 'lucide-react';
import { orgsApi } from '../services/api';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getAuth = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

/**
 * Reusable "Email this report as PDF" dialog.
 * Props:
 *   - open / onOpenChange
 *   - mode: 'single' | 'bulk' | 'bundle'
 *   - reportId  (when mode='single')
 *   - reportIds (when mode='bulk')
 *   - bundleId  (when mode='bundle')
 *   - defaultSubject (optional)
 */
export function EmailReportDialog({ open, onOpenChange, mode = 'single', reportId, reportIds = [], bundleId, defaultSubject }) {
  const [recipients, setRecipients] = useState(['']);
  const [cc, setCc] = useState([]);
  const [subject, setSubject] = useState(defaultSubject || '');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  // Load workspace's default audit email and prefill the first recipient
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await orgsApi.getMine();
        if (cancelled) return;
        if (data?.default_audit_email) {
          setRecipients((prev) => (!prev[0] ? [data.default_audit_email] : prev));
        }
      } catch {/* ignore */}
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const updateRecipient = (i, value) => {
    const next = [...recipients];
    next[i] = value;
    setRecipients(next);
  };
  const addRecipient = () => setRecipients([...recipients, '']);
  const removeRecipient = (i) => setRecipients(recipients.filter((_, idx) => idx !== i));

  const handleSend = async (e) => {
    e.preventDefault();
    const to = recipients.map((r) => r.trim()).filter(Boolean);
    if (!to.length) {
      toast.error('Add at least one recipient email');
      return;
    }
    setSending(true);
    try {
      const payload = {
        to_emails: to,
        cc_emails: cc.length ? cc : undefined,
        subject: subject.trim() || undefined,
        message: message.trim() || undefined,
      };
      if (mode === 'bulk') {
        await axios.post(`${API}/reports/bulk-email-pdf`, { ...payload, report_ids: reportIds }, { headers: getAuth() });
        toast.success(`${reportIds.length} report PDF(s) emailed to ${to.join(', ')}`);
      } else if (mode === 'bundle') {
        await axios.post(`${API}/report-bundles/${bundleId}/email`, payload, { headers: getAuth() });
        toast.success(`Bundle emailed to ${to.join(', ')}`);
      } else {
        await axios.post(`${API}/reports/${reportId}/email-pdf`, payload, { headers: getAuth() });
        toast.success(`Report PDF emailed to ${to.join(', ')}`);
      }
      onOpenChange(false);
      // reset for next open
      setMessage('');
      setSubject('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send email');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-white" data-testid="email-report-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-teal-600" />
            {mode === 'bulk' ? `Email ${reportIds.length} report PDF${reportIds.length === 1 ? '' : 's'}` : mode === 'bundle' ? 'Email this saved bundle' : 'Email this report as PDF'}
          </DialogTitle>
          <DialogDescription>
            Sends the report PDF{mode === 'bulk' && reportIds.length > 5 ? 's (as a ZIP)' : ''} as an
            attachment. Useful for sending to your NDIS auditor or compliance contact.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSend} className="space-y-3" data-testid="email-report-form">
          <div className="space-y-2">
            <Label>To</Label>
            {recipients.map((r, i) => (
              <div key={i} className="flex gap-2">
                <Input
                  type="email"
                  data-testid={`email-recipient-${i}`}
                  value={r}
                  onChange={(e) => updateRecipient(i, e.target.value)}
                  placeholder="auditor@example.com"
                  required={i === 0}
                />
                {recipients.length > 1 && (
                  <Button type="button" variant="outline" onClick={() => removeRecipient(i)} aria-label="Remove">
                    <X className="w-4 h-4" />
                  </Button>
                )}
              </div>
            ))}
            {recipients.length < 10 && (
              <Button type="button" variant="ghost" size="sm" onClick={addRecipient} data-testid="add-recipient-btn"
                className="text-teal-600 hover:text-teal-700">
                <Plus className="w-3 h-3 mr-1" /> Add another recipient
              </Button>
            )}
          </div>

          <div className="space-y-1">
            <Label htmlFor="email-subject">Subject (optional)</Label>
            <Input id="email-subject" data-testid="email-subject" value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Leave blank for an auto-generated subject" />
          </div>

          <div className="space-y-1">
            <Label htmlFor="email-message">Message (optional)</Label>
            <Textarea id="email-message" data-testid="email-message" value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4} maxLength={2000}
              placeholder="Hi, please find attached…" />
          </div>

          <div className="flex gap-2 justify-end pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={sending} className="bg-teal-600 hover:bg-teal-700"
              data-testid="email-send-btn">
              {sending ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</>) :
                (<><Mail className="w-4 h-4 mr-2" /> Send PDF{mode === 'bulk' && reportIds.length > 1 ? 's' : ''}</>)}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
